import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, Shield, User } from "lucide-react";
import { toast } from "sonner";

export const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  
  const isImpersonating = localStorage.getItem("is_impersonating") === "true";
  
  if (!isImpersonating) return null;
  
  // Get original user info from localStorage (can be superadmin or admin)
  const getOriginalUserInfo = () => {
    // Try superadmin first (backward compatibility)
    const superAdminUser = localStorage.getItem("original_superadmin_user");
    const superAdminToken = localStorage.getItem("original_superadmin_token");
    if (superAdminUser && superAdminToken) {
      return {
        user: JSON.parse(superAdminUser),
        token: superAdminToken,
        keys: ["original_superadmin_user", "original_superadmin_token"]
      };
    }
    
    // Try admin (for admin impersonating city_admin/driver)
    const adminUser = localStorage.getItem("original_admin_user");
    const adminToken = localStorage.getItem("original_admin_token");
    if (adminUser && adminToken) {
      return {
        user: JSON.parse(adminUser),
        token: adminToken,
        keys: ["original_admin_user", "original_admin_token"]
      };
    }
    
    return null;
  };
  
  const getRoleLabel = (role) => {
    const labels = {
      driver: "Driver",
      city_admin: "City Admin",
      admin: "Admin",
      superadmin: "Super Admin",
      sub_super_admin: "Sub Super Admin",
      customer: "Customer",
    };
    return labels[role] || role;
  };
  
  const handleSwitchBack = () => {
    try {
      const originalInfo = getOriginalUserInfo();
      
      if (originalInfo && originalInfo.token && originalInfo.user.id) {
        // Restore original session
        login(originalInfo.token, originalInfo.user);
        
        // Clear impersonation data
        originalInfo.keys.forEach(key => localStorage.removeItem(key));
        localStorage.removeItem("is_impersonating");
        
        // Navigate based on original role
        const role = originalInfo.user.role;
        if (role === "superadmin" || role === "sub_super_admin") {
          navigate("/superadmin");
          toast.success("Returned to Super Admin");
        } else if (role === "admin") {
          navigate("/admin");
          toast.success(`Returned to ${originalInfo.user.name || "Admin"}`);
        } else {
          navigate("/");
          toast.success("Session restored");
        }
      } else {
        // Fallback: just clear and redirect
        localStorage.removeItem("is_impersonating");
        localStorage.removeItem("original_superadmin_user");
        localStorage.removeItem("original_superadmin_token");
        localStorage.removeItem("original_admin_user");
        localStorage.removeItem("original_admin_token");
        navigate("/");
        toast.info("Session expired, please login again");
      }
    } catch (err) {
      console.error("Error switching back:", err);
      localStorage.removeItem("is_impersonating");
      navigate("/");
    }
  };
  
  const originalInfo = getOriginalUserInfo();
  const originalName = originalInfo?.user?.name || "Admin";
  const originalRole = originalInfo?.user?.role;
  
  return (
    <div 
      className="bg-amber-500 text-white py-2 px-4 flex items-center justify-between shadow-md z-[100]" 
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3">
        <Eye className="w-5 h-5" />
        <span className="text-sm font-medium flex items-center gap-2">
          <Shield className="w-4 h-4" />
          <span>Viewing as</span>
          <span className="bg-amber-600/40 px-2 py-0.5 rounded text-xs">
            {getRoleLabel(user?.role)}
          </span>
          <User className="w-4 h-4" />
          <strong>{user?.name || user?.phone}</strong>
        </span>
      </div>
      <Button 
        size="sm" 
        variant="secondary" 
        onClick={handleSwitchBack}
        className="bg-white text-amber-700 hover:bg-amber-50 font-medium"
        data-testid="switch-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Switch Back to {originalName}
      </Button>
    </div>
  );
};

export default ImpersonationBanner;
